import { httpClient, qsOptions } from 'providers/clients/httpClient';
import qs from 'qs';

export type WorkflowProcess = {
  /** Format: uuid */
  id?: string;
  name?: string;
  /** User group which has edit access to content within the process. */
  group?: string;
  /** Toggle print-ready state to on for content which enters the process. */
  print_ready?: boolean;
  /** Toggle web-ready state to on for content which enters the process. */
  web_ready?: boolean;
  /** Block syndication of content which occupies the process. */
  no_syndicate?: boolean;
  /** Reset an asset's start time to the current date and time upon entering the process. */
  reset_start_time?: boolean;
  /** Force draft revisions for the save which enters this process. */
  force_draft?: boolean;
  /** Force live status for content which enters the process. */
  force_live?: boolean;
  /** Return content to this process if saved as a draft revision in a later process. */
  return_on_draft?: boolean;
  /** Disable collaboration features for assets which occupy this process. */
  disable_collaboration?: boolean;
  /** Site tags to append to assets entering the process. */
  site_tags?: string[];
};

export type Workflow = {
  /** Format: uuid */
  id?: string;
  name?: string;
  processes?: WorkflowProcess[];
};

// Get /core/workflow/ list
export type GetWorkflowsResult = {
  status?: string | number;
  data?: Workflow[];
};

// Get /core/workflow/{workflow_id}/
export type GetWorkflowByIdArgs = {
  query?: {
    workflow_id?: string;
  };
};

export type GetWorkflowByIdResult = {
  status?: string | number;
  data?: Workflow;
};

export type GetWorkflowsParams = {
  query?: string;
  /** Limit workflows to those accessible to the active user */
  accessible?: boolean;
};

// -----------------------------------------------
// ------------ WORKFLOWS PROVIDER ----------------
// -----------------------------------------------
export const workflowsProvider = {
  // Get /core/workflow/ list
  getWorkflows: async (params?: GetWorkflowsParams): Promise<GetWorkflowsResult> => {
    try {
      const basePath = `/core/workflow/`;

      // Extract and build query params
      const qpPayload: Partial<GetWorkflowsParams> = {};
      const query = params?.query;
      const accessible = params?.accessible;

      if (query) {
        qpPayload.query = query;
      }

      if (typeof accessible === 'boolean') {
        qpPayload.accessible = accessible;
      }

      // Build final URL with query params
      const hasPayload = Object.keys(qpPayload).length > 0;
      const qpStr = hasPayload ? qs.stringify(qpPayload, qsOptions) : '';
      const finalUrl = qpStr ? `${basePath}?${qpStr}` : basePath;

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
      });

      const result: GetWorkflowsResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getWorkflows] Error:', error);
      throw error;
    }
  },

  // Get /core/workflow/{workflow_id}/
  getWorkflowById: async (
    params: GetWorkflowByIdArgs,
  ): Promise<GetWorkflowByIdResult> => {
    try {
      const workflowId = params?.query?.workflow_id;
      if (!workflowId) {
        throw new Error('[getWorkflowById] Missing workflow_id parameter');
      }

      const path = `/core/workflow/${workflowId}/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetWorkflowByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      throw new Error(`[getWorkflowById] Error fetching workflow by ID`);
    }
  },
} as const;

export type WorkflowsProviderType = typeof workflowsProvider;
